/**
 * Escapes untrusted, user-controlled text before interpolating it into a Slack mrkdwn
 * message. Neutralizes Slack's three reserved characters (`&`, `<`, `>`) so attacker-
 * supplied values can't inject control sequences such as `<!channel>`, `<!here>`,
 * `<url|label>` phishing links, or `<@U123>` mentions — `&` is replaced first so the
 * entity replacements aren't themselves re-escaped. Newlines are collapsed to spaces so
 * a value can't spoof additional message structure.
 *
 * Accepts `unknown` and coerces with `String()` — Slack interpolation frequently receives
 * raw, untyped JSON values straight off a request body, so a number/object must not throw.
 *
 * Apply this to interpolated *data* values only — never to trusted mention prefixes
 * (e.g. the `<@U…>` / `<!channel>` returned by `getEventPocSlackMention`), which are meant
 * to render as live mentions.
 *
 * @param value - Possibly-untrusted value of any type (or nullish).
 * @returns The escaped, single-line string; empty string for nullish input.
 */
export function escapeSlackText(value: unknown): string {
  if (value == null) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/[\r\n]+/g, " ")
    .trim();
}

/**
 * Escapes untrusted text the same way as {@link escapeSlackText}, but keeps line breaks —
 * for a multi-line body (an email/venue message) shown verbatim in a notification, where
 * collapsing it to one line would make the message harder to read, not safer. Still
 * neutralizes `&`/`<`/`>`, so `<!channel>`/`<@U123>`/a phishing link inside the body can't
 * inject Slack control sequences.
 *
 * @param value - Possibly-untrusted value of any type (or nullish).
 * @returns The escaped string with its original line breaks; empty string for nullish input.
 */
export function escapeSlackBody(value: unknown): string {
  if (value == null) return "";
  return String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * Builds a Slack mrkdwn link to an event in the admin dashboard. The link label
 * is the event name when supplied, falling back to `event #<id>`. The id is
 * numeric (not user-controlled), but the name IS user-controlled, so only the
 * name segment is escaped (via {@link escapeSlackText}) before becoming the
 * label. The overall `<url|label>` form is trusted Slack output, so the result
 * must NOT be passed through `escapeSlackText` (which would neutralize the link).
 *
 * Uses `NEXT_PUBLIC_SITE_URL`, falling back to the production origin when unset —
 * matching the rest of the codebase (`cron-auth.ts`, `sendgrid.ts`).
 *
 * @param eventId - The event's numeric id.
 * @param eventName - The event's display name; appended with the id (e.g. `Q2 Offsite (#123)`).
 *   Falls back to `event #<id>` when empty.
 * @returns A Slack link like `<https://www.nowadays.ai/admin?event=123|Q2 Offsite (#123)>`.
 */
export function slackEventLink(eventId: number, eventName?: string | null): string {
  const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || "https://www.nowadays.ai";
  const label = eventName && eventName.trim() ? `${escapeSlackText(eventName)} (#${eventId})` : `event #${eventId}`;
  return `<${baseUrl}/admin?event=${eventId}|${label}>`;
}

/**
 * Builds a Slack mrkdwn link to a venue's card on the client-facing proposals
 * page (`?venue=` deep-links the venue-detail overlay open) — the most useful
 * "go look at this" destination for a decision-email notification, since it
 * shows the venue's status and communications, not just the event.
 *
 * Uses `NEXT_PUBLIC_SITE_URL`, falling back to the production origin, matching
 * {@link slackEventLink}.
 *
 * @param eventId - The event's numeric id.
 * @param venueStaticId - The venue to deep-link to.
 * @param label - Pre-formatted link label (build with {@link escapeSlackText} if user-controlled).
 * @returns A Slack link like `<https://www.nowadays.ai/dashboard/source/123/proposals?venue=456|View>`.
 */
export function slackVenueProposalLink(eventId: number, venueStaticId: number, label: string): string {
  const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || "https://www.nowadays.ai";
  return `<${baseUrl}/dashboard/source/${eventId}/proposals?venue=${venueStaticId}|${label}>`;
}

/**
 * Core sentence for the "no direct email on file, so the RFP went to the chain
 * rep instead" Slack notification. Shared between the admin bulk-send path
 * (`sendRFPEmails` in `components/ui/utils.ts`) and the enterprise-portal path
 * (`notifyMissingVenueEmailSlack`) so the wording can't drift between them.
 *
 * @param venueLabel - Pre-formatted, already-escaped venue label — a single
 *   venue name, or a "this hotel"/"these hotels" phrase for a batch.
 * @param eventLink - Pre-built Slack link/label for the event.
 * @param isPlural - Whether the message covers more than one venue (drives the
 *   "its"/"their" pronoun).
 * @returns The message body, without a leading mention.
 */
export function buildMissingVenueEmailSentence(venueLabel: string, eventLink: string, isPlural: boolean): string {
  return `We don't have a direct email on file for ${venueLabel} on ${eventLink}, so the RFP was sent to their chain rep instead. Can you track down and add ${isPlural ? "their" : "its"} direct email address for future events?`;
}

/**
 * Sentence for the case where we held no direct email, the RFP went to the chain rep,
 * and the ENG-703 web search then found a direct address and saved it (ENG-703).
 *
 * Deliberately asks the VA to confirm rather than telling them it's handled: the
 * address came off a web page, and the VA is the check on that. It is saved already,
 * so their job is verification, not data entry — and confirming it before the next
 * RFP is what stops a wrong address going out unattended.
 *
 * @param venueLabel - Pre-formatted, already-escaped venue label.
 * @param eventLink - Pre-built Slack link/label for the event.
 * @param email - The discovered address, already escaped.
 * @returns The message body, without a leading mention.
 */
export function buildDiscoveredVenueEmailSentence(venueLabel: string, eventLink: string, email: string): string {
  return `We had no direct email on file for ${venueLabel} on ${eventLink}, so that RFP went to their chain rep. We then searched the web and saved ${email} to the venue, which is where the next RFP will go — can you confirm it's the right sales contact?`;
}
